import { Component, OnInit, ViewChild } from "@angular/core";
import { SuperAdminService } from "src/app/super-admin.service";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { NgxSpinnerService } from "ngx-spinner";
import { DomSanitizer } from "@angular/platform-browser";
import { environment } from "src/environments/environment";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort, Sort } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { debounce } from "lodash";
import { ImagePreviewComponent } from "../image-preview/image-preview.component";
import { PODStatusComponent } from "../pod-status/pod-status.component";

@Component({
  selector: "app-lr-list",
  templateUrl: "./lr-list.component.html",
  styleUrls: ["./lr-list.component.css"],
})
export class LRListComponent implements OnInit {
  apiUrl = environment.domain;
  fromDate: string = "";
  toDate: string = "";
  displayedColumns: string[] = [
    "manualLR_no",
    "LR_no",
    "created_by",
    "vehicle_id",
    "clientName",
    "consignee_name",
    "consignor_name",
    "date",
    "from",
    "to",
    "vehicle_rate",
    "advance",
    "total_expense",
    "action",
  ];
  isAdmin = false;
  isSuperSupervisor: boolean = false;
  dataSource = new MatTableDataSource<any>([]);
  totalRecords = 0;
  currentPage = 1;
  pageSize = 10;
  searchTerm = "";

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  userSessionData: any;
  clientList: any[] = [];
  receipts: any[] = [];

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private superAdminService: SuperAdminService,
    private spinner: NgxSpinnerService,
    private sanitizer: DomSanitizer,
    public dialog: MatDialog
  ) {
    // debounce search function (runs after 400ms pause in typing)
    this.filterData = debounce(this.filterData.bind(this), 400);
  }

  ngOnInit(): void {
    this.userSessionData = JSON.parse(
      sessionStorage.getItem("userSession") || "{}"
    );

    const roleIds = this.userSessionData?.user_role_id || [];

    this.isAdmin = roleIds.includes(1);
    this.isSuperSupervisor = roleIds.includes(7);

    const hasFullAccess = this.isAdmin || this.isSuperSupervisor;

    this.loadLRs();
  }

  // Fetch data with pagination & search
  loadLRs(): void {
    this.spinner.show();

    const roleIds = this.userSessionData?.user_role_id || [];
    const isAdmin = roleIds.includes(1);
    const hasFullAccess = roleIds.includes(1) || roleIds.includes(7);

    const isSupervisor = roleIds.includes(2);

    let apiCall;

    if (hasFullAccess) {
      apiCall = this.superAdminService.getlrs(
        this.currentPage,
        this.pageSize,
        this.searchTerm,
        this.fromDate,
        this.toDate
      );
    } else if (isSupervisor) {
      apiCall = this.superAdminService.getLrsBySupervisor(
        this.currentPage,
        this.pageSize,
        this.searchTerm,
        this.fromDate,
        this.toDate
      );
    }

    apiCall?.subscribe({
      next: (res: any) => {
        if (res.status) {
          const rawData = res.data;

          this.receipts = rawData.map((item: any) => item.receipt);

          this.dataSource = new MatTableDataSource(this.receipts);
          this.totalRecords = res.pagination?.total || this.receipts.length;
          this.dataSource.sort = this.sort;

          this.spinner.hide();
        } else {
          this.spinner.hide();
          this.toastr.error(res.message);
        }
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error("Failed to load LR data!");
      },
    });
  }

  // Called when user changes page or limit
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadLRs();
  }

  // Search input handler (debounced)
  filterData(value: string): void {
    this.searchTerm = value.trim().toLowerCase();
    this.currentPage = 1;
    this.loadLRs();
  }

  //  Sorting logic
  sortData(sort: Sort): void {
    const data = this.dataSource.filteredData.slice();
    if (!sort.active || sort.direction === "") return;

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      return this.compareValues(a[sort.active], b[sort.active], isAsc);
    });
  }

  private compareValues(a: any, b: any, isAsc: boolean): number {
    a = a ? a.toString().toLowerCase() : "";
    b = b ? b.toString().toLowerCase() : "";
    return (a < b ? -1 : a > b ? 1 : 0) * (isAsc ? 1 : -1);
  }

  //  Image Preview Popup
  previewLoadingSlip(imageUrl: any, backImageUrl: any): void {
    this.dialog.open(ImagePreviewComponent, {
      width: "80%",
      data: { file: imageUrl, backFile: backImageUrl },
    });
  }

  //  POD Upload Popup
  podStatus(id: string): void {
    this.dialog.open(PODStatusComponent, {
      width: "40%",
      data: { id },
    });
  }

  exportToExcel(): void {
    this.spinner.show();

    this.superAdminService
      .exportLrs(this.searchTerm, this.fromDate, this.toDate)
      .subscribe({
        next: (res: any) => {
          this.spinner.hide();

          if (!res.status || !res.rawdata || res.rawdata.length === 0) {
            this.toastr.warning("No data to export!");
            return;
          }

          const fullData = res.rawdata;

          const exportData = fullData.map((item: any, i: number) => ({
            "S.No": i + 1,
            "LR No": item?.LR_no || "",
            "Manual LR No": item?.manualLR_no || "",
            Type: item?.type || "",
            Supervisor: item?.supervisorName || "",
            "Company Name": item?.clientName || "",
            "Created By": item?.created_by?.full_name || "",
            "Consignor Name": item?.consignor_name || "",
            "Consignor Address": item?.consignor_address || "",
            "Consignee Name": item?.consignee_name || "",
            "Consignee Address": item?.consignee_address || "",
            "Vehicle No": item?.vehicle_id || "",
            "Vehicle Type": item?.vehicleType || "",
            "LR Date": item?.date
              ? new Date(item.date).toLocaleDateString()
              : "",
            From: item?.from || "",
            To: item?.to || "",
            "Trip Start Date": item.tripStartDate
              ? new Date(item.tripStartDate).toLocaleDateString()
              : "",
            "Trip End Date": item.tripEndDate
              ? new Date(item.tripEndDate).toLocaleDateString()
              : "",
            GSTIN: item.GSTIN || "",
            "Road Permit No": item?.road_permit_no || "",
            "DLY No": item?.dly_no || "",
            "Order Id": item?.orderId || "",
            "Declared Value": item?.declared_value || "",
            "Transporter Name": item?.transportorName || "",
            "Transporter Mobile": item?.transportorMobile || "",
            "Driver Name": item?.driverName || "",
            "Driver Mobile": item?.driverMobile || "",
            "Vehicle Rate": item?.vehicleRate || "",
            Advance: item?.advance || "",
            Balance: item?.balance || "",
            "Balance Paid": item?.balancePaid || "",
            TDS: item?.TDS || "",
            "Munsipal Amount ": item?.munsipalAmount,
            "Final Amount": item?.finalAmount || "",
            Quantity: item?.quantity || "",
            Freight: item?.Frieght || "",
            Detention:item?.detention|| "",
            "Other Payment": item?.otherPayment || "",
            "Labour Load": item?.labourLoad || "",
            "Labour Unload": item?.labourUnload || "",
            "Total Expense": item?.totalExpense || "",

          }));

          const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
          ws["!cols"] = [
            { wch: 5 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 10 },
          ];

          const wb: XLSX.WorkBook = {
            Sheets: { "LR Data": ws },
            SheetNames: ["LR Data"],
          };

          const excelBuffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array",
          });

          saveAs(
            new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
            `LR_Data_${new Date().toISOString().slice(0, 10)}.xlsx`
          );
        },
        error: () => {
          this.spinner.hide();
          this.toastr.error("Export failed!");
        },
      });
  }

  onDateChange(event: any, type: "from" | "to"): void {
    if (type === "from") {
      this.fromDate = event.target.value;
    } else {
      this.toDate = event.target.value;
    }

    this.currentPage = 1;
    this.loadLRs();
  }

  clearFilters() {
    this.searchTerm = "";
    this.fromDate = "";
    this.toDate = "";

    // Reset page
    this.currentPage = 1;

    // Clear input fields
    const fromInput = document.getElementById("fromDate") as HTMLInputElement;
    const toInput = document.getElementById("toDate") as HTMLInputElement;
    const searchInput = document.querySelector(
      "input.search-box"
    ) as HTMLInputElement;

    if (fromInput) fromInput.value = "";
    if (toInput) toInput.value = "";
    if (searchInput) searchInput.value = "";

    // Load default data
    this.loadLRs();

    // Reset paginator visually
    this.paginator?.firstPage();
  }

  deleteLR(receipt: any): void {
    if (!receipt?._id) return;

    const confirmDelete = confirm(
      `Are you sure you want to delete LR ${receipt.LR_no}?`
    );

    if (!confirmDelete) return;

    this.spinner.show();

    this.superAdminService.deleteLR(receipt._id).subscribe({
      next: (res) => {
        this.spinner.hide();

        if (res.status) {
          this.toastr.success(res.message || "LR deleted successfully");
          this.loadLRs();
        } else {
          this.toastr.error(res.message || "Failed to delete LR");
        }
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error("Delete failed!");
      },
    });
  }
}
